import React, { useEffect, useLayoutEffect, useReducer, useRef, useState } from "react";
import { Button } from "./ui/button";

const CORES = { l1: "#22c55e", l2: "#3b82f6", l3: "#f59e0b", g: "#a78bfa", m: "#22d3ee" };
const SEL = [
  ["l1", "🟢 1 · Início", "border-green-500 text-green-300 bg-green-950"],
  ["l2", "🔵 2 · Corte atual", "border-blue-500 text-blue-300 bg-blue-950"],
  ["l3", "🟠 3 · Fim", "border-amber-500 text-amber-300 bg-amber-950"],
  ["g", "🟣 Geratriz", "border-purple-500 text-purple-300 bg-purple-950"],
  ["m", "📐 Medidor", "border-cyan-500 text-cyan-300 bg-cyan-950"],
  ["p", "✏️ Caneta", "border-red-500 text-red-300 bg-red-950"],
];
const CORES_CANETA = ["#ef4444", "#facc15", "#ffffff"];

const defaults = (W, H) => ({
  l1: { a: { x: 0.07 * W, y: 0.4 * H }, b: { x: 0.93 * W, y: 0.315 * H }, bend: { x: 0, y: 0 } },
  l2: { a: { x: 0.07 * W, y: 0.61 * H }, b: { x: 0.93 * W, y: 0.525 * H }, bend: { x: 0, y: 0 } },
  l3: { a: { x: 0.07 * W, y: 0.85 * H }, b: { x: 0.93 * W, y: 0.765 * H }, bend: { x: 0, y: 0 } },
  g1: { a: { x: 0.19 * W, y: 0.22 * H }, b: { x: 0.17 * W, y: 0.92 * H } },
  g2: { a: { x: 0.82 * W, y: 0.18 * H }, b: { x: 0.84 * W, y: 0.88 * H } },
  m: { a: { x: 0.34 * W, y: 0.5 * H }, b: { x: 0.66 * W, y: 0.42 * H } },
});

const ctrl = (l) => ({ x: (l.a.x + l.b.x) / 2 + l.bend.x * 2, y: (l.a.y + l.b.y) / 2 + l.bend.y * 2 });
const ptCurva = (l, t) => {
  const c = ctrl(l), u = 1 - t;
  return { x: u * u * l.a.x + 2 * u * t * c.x + t * t * l.b.x, y: u * u * l.a.y + 2 * u * t * c.y + t * t * l.b.y };
};
const amostra = (l, n = 12) => Array.from({ length: n + 1 }, (_, i) => ptCurva(l, i / n));
const midY = (l) => { const c = ctrl(l); return 0.25 * l.a.y + 0.5 * c.y + 0.25 * l.b.y; };
const curvaPath = (l) => { const c = ctrl(l); return `M ${l.a.x} ${l.a.y} Q ${c.x} ${c.y} ${l.b.x} ${l.b.y}`; };

export default function DemarcacaoEditor({ foto, inicial, onSave, onClose }) {
  const stageRef = useRef(null);
  const S = useRef(null);
  const [, force] = useReducer((x) => x + 1, 0);
  const [medidorCm, setMedidorCm] = useState(inicial?.calibracao?.cm ? String(inicial.calibracao.cm) : "");

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const W = el.clientWidth, H = el.clientHeight;
    const montar = (nw, nh) => {
      const scale = Math.max(W / nw, H / nh);
      const R = { x: (W - nw * scale) / 2, y: (H - nh * scale) / 2, w: nw * scale, h: nh * scale };
      const dn = (p) => ({ x: R.x + p.x * R.w, y: R.y + p.y * R.h });
      const L = defaults(W, H);
      if (inicial?.linhas) {
        for (const k of ["l1", "l2", "l3"]) {
          const ln = inicial.linhas[k];
          if (ln?.a && ln?.b) L[k] = { a: dn(ln.a), b: dn(ln.b), bend: { x: (ln.bend?.x || 0) * R.w, y: (ln.bend?.y || 0) * R.h } };
        }
      }
      if (inicial?.geratriz?.g1?.a) L.g1 = { a: dn(inicial.geratriz.g1.a), b: dn(inicial.geratriz.g1.b) };
      if (inicial?.geratriz?.g2?.a) L.g2 = { a: dn(inicial.geratriz.g2.a), b: dn(inicial.geratriz.g2.b) };
      if (inicial?.medidor?.a) L.m = { a: dn(inicial.medidor.a), b: dn(inicial.medidor.b) };
      S.current = {
        W, H, R, L,
        ativa: "l2",
        geraOn: inicial?.geratriz ? inicial.geratriz.visivel !== false : true,
        medOn: !!inicial?.medidor,
        Z: { s: 1, tx: 0, ty: 0 },
        tracos: (inicial?.tracos || []).map((t) => ({ c: t.c, pts: t.pts.map(dn) })),
        corCaneta: "#ef4444",
        desenhando: null,
        drag: null,
        pinch: null,
        calib: inicial?.calibracao?.cm ? { cm: inicial.calibracao.cm, lenW: inicial.calibracao.len_wfrac } : null,
      };
      force();
    };
    const im = new Image();
    im.onload = () => montar(im.naturalWidth || W, im.naturalHeight || H);
    im.onerror = () => montar(W, H);
    im.src = foto;
  }, [inicial, foto]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const raw = (e) => {
      const r = el.getBoundingClientRect();
      const t = e.touches ? e.touches[0] : e;
      return { x: t.clientX - r.left, y: t.clientY - r.top };
    };
    const pos = (e) => {
      const s = S.current, p = raw(e);
      return {
        x: Math.min(Math.max((p.x - s.Z.tx) / s.Z.s, 4), s.W - 4),
        y: Math.min(Math.max((p.y - s.Z.ty) / s.Z.s, 4), s.H - 4),
      };
    };
    const dist2 = (e) => Math.hypot(e.touches[0].clientX - e.touches[1].clientX, e.touches[0].clientY - e.touches[1].clientY);
    const mid2 = (e) => {
      const r = el.getBoundingClientRect();
      return { x: (e.touches[0].clientX + e.touches[1].clientX) / 2 - r.left, y: (e.touches[0].clientY + e.touches[1].clientY) / 2 - r.top };
    };
    const clampZ = () => {
      const s = S.current;
      s.Z.s = Math.min(Math.max(s.Z.s, 1), 4);
      s.Z.tx = Math.min(0, Math.max(s.W - s.W * s.Z.s, s.Z.tx));
      s.Z.ty = Math.min(0, Math.max(s.H - s.H * s.Z.s, s.Z.ty));
    };
    const down = (e) => {
      const s = S.current;
      if (!s) return;
      if (e.target.closest && e.target.closest("button")) return;
      if (e.touches && e.touches.length === 2) {
        s.drag = null; s.desenhando = null;
        const m = mid2(e);
        s.pinch = { d: dist2(e), s0: s.Z.s, wx: (m.x - s.Z.tx) / s.Z.s, wy: (m.y - s.Z.ty) / s.Z.s };
        e.preventDefault();
        return;
      }
      if (s.ativa === "p") {
        s.desenhando = { pts: [pos(e)], c: s.corCaneta };
        e.preventDefault();
        return;
      }
      const t = e.target.closest ? e.target.closest("[data-id]") : null;
      if (t && t.dataset.id) {
        s.drag = { id: t.dataset.id, p: t.dataset.p, last: pos(e) };
        e.preventDefault();
      } else if (s.Z.s > 1) {
        s.drag = { pan: true, last: raw(e) };
        e.preventDefault();
      }
    };
    const move = (e) => {
      const s = S.current;
      if (!s) return;
      if (s.pinch && e.touches && e.touches.length === 2) {
        const m = mid2(e);
        s.Z.s = s.pinch.s0 * (dist2(e) / s.pinch.d);
        s.Z.tx = m.x - s.pinch.wx * s.Z.s;
        s.Z.ty = m.y - s.pinch.wy * s.Z.s;
        clampZ(); force(); e.preventDefault();
        return;
      }
      if (s.desenhando) {
        s.desenhando.pts.push(pos(e));
        force(); e.preventDefault();
        return;
      }
      if (!s.drag) return;
      if (s.drag.pan) {
        const p = raw(e);
        s.Z.tx += p.x - s.drag.last.x;
        s.Z.ty += p.y - s.drag.last.y;
        s.drag.last = p;
        clampZ(); force(); e.preventDefault();
        return;
      }
      const p = pos(e);
      const l = s.L[s.drag.id];
      if (s.drag.p === "meio") {
        if (s.drag.id === "m" || s.drag.id.startsWith("g")) {
          const dx = p.x - s.drag.last.x, dy = p.y - s.drag.last.y;
          l.a.x += dx; l.a.y += dy; l.b.x += dx; l.b.y += dy;
          s.drag.last = p;
        } else {
          l.bend = { x: p.x - (l.a.x + l.b.x) / 2, y: p.y - (l.a.y + l.b.y) / 2 };
        }
      } else {
        l[s.drag.p] = p;
      }
      force(); e.preventDefault();
    };
    const up = (e) => {
      const s = S.current;
      if (!s) return;
      if (e && e.touches && e.touches.length >= 2) return;
      if (s.desenhando) {
        if (s.desenhando.pts.length > 1) s.tracos.push(s.desenhando);
        s.desenhando = null;
        force();
      }
      s.pinch = null;
      s.drag = null;
    };
    el.addEventListener("mousedown", down);
    el.addEventListener("touchstart", down, { passive: false });
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      el.removeEventListener("mousedown", down);
      el.removeEventListener("touchstart", down);
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, []);

  const s = S.current;
  const vertRef = () => {
    if (!s || !s.geraOn) return { x: 0, y: 1 };
    const vs = [s.L.g1, s.L.g2].map((g) => {
      const dx = g.b.x - g.a.x, dy = g.b.y - g.a.y, len = Math.hypot(dx, dy) || 1;
      return { x: dx / len, y: dy / len };
    });
    const v = { x: (vs[0].x + vs[1].x) / 2, y: (vs[0].y + vs[1].y) / 2 };
    const l = Math.hypot(v.x, v.y) || 1;
    return { x: v.x / l, y: v.y / l };
  };
  const anguloHoriz = (d) => {
    const v = vertRef();
    const dv = Math.abs(d.x * v.x + d.y * v.y);
    const dh = Math.abs(d.x * -v.y + d.y * v.x);
    return Math.round((Math.atan2(dv, dh) * 180) / Math.PI);
  };
  const angulosLinha = (l) => {
    const pm = ptCurva(l, 0.5);
    return {
      inicial: anguloHoriz({ x: pm.x - l.a.x, y: pm.y - l.a.y }),
      final: anguloHoriz({ x: l.b.x - pm.x, y: l.b.y - pm.y }),
    };
  };

  const zoomAt = (f) => {
    const ns = Math.min(Math.max(s.Z.s * f, 1), 4);
    const cx = s.W / 2, cy = s.H / 2;
    const wx = (cx - s.Z.tx) / s.Z.s, wy = (cy - s.Z.ty) / s.Z.s;
    s.Z = { s: ns, tx: Math.min(0, Math.max(s.W - s.W * ns, cx - wx * ns)), ty: Math.min(0, Math.max(s.H - s.H * ns, cy - wy * ns)) };
    force();
  };

  const onCmChange = (v) => {
    setMedidorCm(v);
    const cm = parseFloat(v);
    if (cm > 0 && s) {
      const len = Math.hypot(s.L.m.b.x - s.L.m.a.x, s.L.m.b.y - s.L.m.a.y);
      if (len > 5) s.calib = { cm, lenW: len / s.R.w };
    } else if (s) s.calib = null;
    force();
  };

  const salvar = () => {
    const R = s.R;
    const nm = (p) => ({ x: +((p.x - R.x) / R.w).toFixed(4), y: +((p.y - R.y) / R.h).toFixed(4) });
    const m1 = midY(s.L.l1), m2 = midY(s.L.l2), m3 = midY(s.L.l3);
    const total = m3 - m1;
    const ok = total > 10 && m2 >= m1 && m2 <= m3;
    const esc = s.calib ? s.calib.cm / (s.calib.lenW * R.w) : null;
    onSave({
      linhas: {
        l1: { a: nm(s.L.l1.a), b: nm(s.L.l1.b), bend: { x: +(s.L.l1.bend.x / R.w).toFixed(4), y: +(s.L.l1.bend.y / R.h).toFixed(4) } },
        l2: { a: nm(s.L.l2.a), b: nm(s.L.l2.b), bend: { x: +(s.L.l2.bend.x / R.w).toFixed(4), y: +(s.L.l2.bend.y / R.h).toFixed(4) } },
        l3: { a: nm(s.L.l3.a), b: nm(s.L.l3.b), bend: { x: +(s.L.l3.bend.x / R.w).toFixed(4), y: +(s.L.l3.bend.y / R.h).toFixed(4) } },
      },
      geratriz: { g1: { a: nm(s.L.g1.a), b: nm(s.L.g1.b) }, g2: { a: nm(s.L.g2.a), b: nm(s.L.g2.b) }, visivel: s.geraOn },
      medidor: s.medOn ? { a: nm(s.L.m.a), b: nm(s.L.m.b) } : null,
      calibracao: s.calib ? { cm: s.calib.cm, len_wfrac: +s.calib.lenW.toFixed(5) } : null,
      tracos: s.tracos.map((t) => ({ c: t.c, pts: t.pts.map(nm) })),
      consumido_pct: ok ? +(((m2 - m1) / total) * 100).toFixed(1) : null,
      a_consumir_pct: ok ? +(((m3 - m2) / total) * 100).toFixed(1) : null,
      consumido_cm: ok && esc ? +((m2 - m1) * esc).toFixed(1) : null,
      a_consumir_cm: ok && esc ? +((m3 - m2) * esc).toFixed(1) : null,
      angulos: { l1: angulosLinha(s.L.l1), l2: angulosLinha(s.L.l2), l3: angulosLinha(s.L.l3) },
    });
  };

  // ---------- render ----------
  let svgEls = null, pcts = { p1: "—", p2: "—", c1: "", c2: "" }, info = "", ftags = null, k = 1;
  if (s) {
    k = 1 / s.Z.s;
    const esc = s.calib ? s.calib.cm / (s.calib.lenW * s.R.w) : null;
    const els = [];
    const band = (a, b, cor, key) => {
      const pa = amostra(s.L[a]), pb = [...amostra(s.L[b])].reverse();
      return <polygon key={key} points={[...pa, ...pb].map((p) => `${p.x},${p.y}`).join(" ")} fill={cor} />;
    };
    els.push(band("l1", "l2", "rgba(34,197,94,.13)", "b1"));
    els.push(band("l2", "l3", "rgba(245,158,11,.11)", "b2"));
    const grupos = ["l1", "l2", "l3", ...(s.geraOn ? ["g1", "g2"] : []), ...(s.medOn ? ["m"] : [])];
    for (const id of grupos) {
      const l = s.L[id];
      const gera = id.startsWith("g"), med = id === "m";
      const reta = gera || med;
      const cor = gera ? CORES.g : med ? CORES.m : CORES[id];
      const on = s.ativa === id || (s.ativa === "g" && gera);
      const d = reta ? `M ${l.a.x} ${l.a.y} L ${l.b.x} ${l.b.y}` : curvaPath(l);
      els.push(<path key={`p${id}`} d={d} stroke={cor} strokeWidth={(on ? 4 : 2) * k} fill="none" strokeDasharray={gera ? `${7 * k} ${5 * k}` : undefined} opacity={on ? 1 : 0.45} />);
      if (on) {
        els.push(<path key={`h${id}`} data-id={id} data-p="meio" d={d} stroke="rgba(0,0,0,0)" strokeWidth={34 * k} fill="none" style={{ cursor: "grab" }} />);
        for (const p of ["a", "b"]) {
          els.push(<circle key={`ht${id}${p}`} data-id={id} data-p={p} cx={l[p].x} cy={l[p].y} r={16 * k} fill="rgba(0,0,0,0)" style={{ cursor: "grab" }} />);
          els.push(<circle key={`c${id}${p}`} cx={l[p].x} cy={l[p].y} r={6.5 * k} fill={cor} stroke="#fff" strokeWidth={2 * k} pointerEvents="none" />);
        }
      }
    }
    const strokes = [...s.tracos, ...(s.desenhando && s.desenhando.pts.length > 1 ? [s.desenhando] : [])];
    strokes.forEach((t, i) => {
      els.push(<path key={`t${i}`} d={t.pts.map((p, j) => `${j ? "L" : "M"}${p.x} ${p.y}`).join(" ")} stroke={t.c} strokeWidth={3.5 * k} fill="none" strokeLinecap="round" strokeLinejoin="round" opacity=".95" pointerEvents="none" />);
    });
    const badge = (x, y, txt, cor, key) => {
      const w = txt.length * 6.2 + 12;
      return (
        <g key={key} pointerEvents="none" transform={`translate(${x},${y}) scale(${k})`}>
          <rect x={-w / 2} y={0} rx={8} width={w} height={17} fill="rgba(0,0,0,.72)" stroke={cor} strokeWidth={1} />
          <text x={0} y={12.5} textAnchor="middle" fontSize="10.5" fontWeight="800" fill="#fff">{txt}</text>
        </g>
      );
    };
    if (["l1", "l2", "l3"].includes(s.ativa)) {
      const l = s.L[s.ativa];
      const ang = angulosLinha(l);
      els.push(badge(ptCurva(l, 0.25).x, ptCurva(l, 0.25).y - 26 * k, `∠ ${ang.inicial}°`, CORES[s.ativa], "ba1"));
      els.push(badge(ptCurva(l, 0.75).x, ptCurva(l, 0.75).y - 26 * k, `∠ ${ang.final}°`, CORES[s.ativa], "ba2"));
      const nome = { l1: "início da safra", l2: "corte atual", l3: "fim da safra" }[s.ativa];
      info = `📐 Ângulo (${nome}) vs. horizontal: metade inicial ${ang.inicial}° · final ${ang.final}° — geratriz = 90°`;
    } else if (s.ativa === "m" && s.medOn) {
      const l = s.L.m;
      const ang = anguloHoriz({ x: l.b.x - l.a.x, y: l.b.y - l.a.y });
      const compPx = Math.hypot(l.b.x - l.a.x, l.b.y - l.a.y);
      const txt = esc ? `∠ ${ang}° · 📏 ${(compPx * esc).toFixed(1)} cm` : `∠ ${ang}° (graus)`;      els.push(badge((l.a.x + l.b.x) / 2, (l.a.y + l.b.y) / 2 - 26 * k, txt, CORES.m, "bm"));
      info = esc
        ? `📐 Medidor — ∠ ${ang}° · 📏 ${(compPx * esc).toFixed(1)} cm`
        : `📐 Medidor — ∠ ${ang}° · calibre: marque algo de tamanho conhecido e informe os cm abaixo`;
    } else if (s.ativa === "p") {
      info = `✏️ Caneta — ${s.tracos.length} anotação(ões) · desenhe sobre ferimento, doença ou ponto de atenção`;
    } else {
      info = "Toque numa linha para editar — só ela mostra as bolinhas";
    }
    svgEls = els;
    const m1 = midY(s.L.l1), m2 = midY(s.L.l2), m3 = midY(s.L.l3);
    const total = m3 - m1;
    if (total > 10 && m2 >= m1 && m2 <= m3) {
      pcts.p1 = Math.round(((m2 - m1) / total) * 100) + "%";
      pcts.p2 = Math.round(((m3 - m2) / total) * 100) + "%";
      if (esc) {
        pcts.c1 = "≈ " + ((m2 - m1) * esc).toFixed(1) + " cm";
        pcts.c2 = "≈ " + ((m3 - m2) * esc).toFixed(1) + " cm";
      }
    }
    ftags = [
      { y: (m1 + m2) / 2 - 10, t: `consumido ${pcts.p1}${pcts.c1 ? " · " + pcts.c1 : ""}` },
      { y: (m2 + m3) / 2 - 10, t: `a consumir ${pcts.p2}${pcts.c2 ? " · " + pcts.c2 : ""}` },
    ];
  }

  const setAtiva = (id) => {
    s.ativa = id;
    if (id === "g" && !s.geraOn) s.geraOn = true;
    if (id === "m") s.medOn = true;
    force();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 overflow-y-auto" data-testid="demarcacao-editor">
      <div className="max-w-[430px] mx-auto min-h-full bg-[#111] text-white">
        <div className="flex items-center justify-between px-3.5 py-3 bg-[#1a241a]">
          <b className="text-sm">📐 Demarcar painel da safra</b>
          <button onClick={onClose} className="text-xs font-bold bg-white/10 rounded-full px-3 py-1.5" data-testid="demarc-fechar-btn">✕ Fechar</button>
        </div>
        <div className="bg-[#2a3a2a] text-[#c9eec9] text-[11px] px-3 py-1.5 text-center font-semibold" data-testid="demarc-hint">
          {s?.ativa === "p" ? "CANETA — desenhe com o dedo · pince p/ zoom" : s?.ativa === "m" ? "MEDIDOR — pontas ajustam, meio arrasta tudo" : s?.ativa === "g" ? "GERATRIZ — sempre reta · define o ângulo 90°" : "Pince p/ zoom · arraste pontas ou o MEIO da linha p/ curvar"}
        </div>
        <div className="flex gap-1.5 px-2.5 py-2 bg-[#10180f] overflow-x-auto">
          {SEL.map(([id, label, cls]) => (
            <button key={id} onClick={() => setAtiva(id)}
              className={`flex-shrink-0 rounded-full px-2.5 py-1.5 text-[10.5px] font-extrabold border-2 ${s?.ativa === id ? cls : "border-[#333] bg-[#1b1b1b] text-[#999]"}`}
              data-testid={`demarc-sel-${id}`}>{label}</button>
          ))}
        </div>
        {s?.ativa === "p" && (
          <div className="flex gap-2 items-center px-3 py-2 bg-[#10180f]" data-testid="demarc-penbar">
            {CORES_CANETA.map((c) => (
              <button key={c} onClick={() => { s.corCaneta = c; force(); }}
                className={`w-[26px] h-[26px] rounded-full border-2 flex-shrink-0 ${s.corCaneta === c ? "border-white ring-2 ring-white/40" : "border-[#555]"}`}
                style={{ background: c }} data-testid={`demarc-cor-${c.slice(1)}`} />
            ))}
            <button onClick={() => { s.tracos.pop(); force(); }} className="text-[11px] font-extrabold px-3 py-1.5 rounded-lg bg-[#1b1b1b] border border-[#444] text-[#ccc]" data-testid="demarc-undo">↶ Desfazer</button>
            <button onClick={() => { s.tracos = []; force(); }} className="text-[11px] font-extrabold px-3 py-1.5 rounded-lg bg-[#1b1b1b] border border-[#444] text-[#ccc]" data-testid="demarc-clear">🗑 Limpar</button>
          </div>
        )}
        <div ref={stageRef} className="relative w-full h-[440px] bg-black select-none" style={{ touchAction: "none", overflow: "clip" }} data-testid="demarc-stage">
          <div className="absolute inset-0" style={{ transform: s ? `translate(${s.Z.tx}px,${s.Z.ty}px) scale(${s.Z.s})` : undefined, transformOrigin: "0 0" }}>
            <img src={foto} alt="painel" draggable={false} className="w-full h-full object-cover opacity-95 pointer-events-none" />
            <svg className="absolute inset-0 w-full h-full">{svgEls}</svg>
            {s && ftags.map((f, i) => (
              <span key={i} className="absolute right-[52px] text-[10px] font-extrabold px-2 py-0.5 rounded-lg text-white bg-black/60 border border-white/25 pointer-events-none"
                style={{ top: f.y, transform: `scale(${k})`, transformOrigin: "100% 50%" }} data-testid={`demarc-ftag-${i}`}>{f.t}</span>
            ))}
          </div>
          <span className="absolute left-2 bottom-2 z-[5] text-[10px] font-extrabold px-2 py-1 rounded-lg bg-black/60 border border-white/25 text-cyan-200" data-testid="demarc-zoom-nivel">🔍 {s ? Math.round(s.Z.s * 10) / 10 : 1}×</span>
          <div className="absolute right-2 bottom-2 z-[5] flex flex-col gap-1.5">
            <button onClick={() => zoomAt(1.5)} className="w-9 h-9 rounded-xl border border-white/30 bg-black/60 text-white font-extrabold" data-testid="demarc-zoom-in">➕</button>
            <button onClick={() => zoomAt(1 / 1.5)} className="w-9 h-9 rounded-xl border border-white/30 bg-black/60 text-white font-extrabold" data-testid="demarc-zoom-out">➖</button>
            <button onClick={() => { s.Z = { s: 1, tx: 0, ty: 0 }; force(); }} className="w-9 h-9 rounded-xl border border-white/30 bg-black/60 text-white text-xs font-extrabold" data-testid="demarc-zoom-reset">1×</button>
          </div>
        </div>
        <div className="bg-[#0d1a26] text-[#93c5fd] text-[11px] font-bold px-3 py-1.5 text-center border-t border-[#1e3a5f] min-h-[30px]" data-testid="demarc-anginfo">{info}</div>
        <div className="flex gap-2 px-3 py-2.5 bg-[#151d15]">
          <button onClick={() => { s.geraOn = !s.geraOn; if (!s.geraOn && s.ativa === "g") s.ativa = "l2"; force(); }}
            className="flex-1 text-center rounded-xl py-2 text-[11px] font-bold border border-[#2e3e2e] bg-[#1c281c] text-[#bbb]" data-testid="demarc-gera-toggle">
            🟣 {s?.geraOn ? "Ocultar" : "Mostrar"} geratriz
          </button>
          <button onClick={() => { const d = defaults(s.W, s.H); s.L = { ...s.L, ...d }; force(); }}
            className="flex-1 text-center rounded-xl py-2 text-[11px] font-bold border border-[#2e3e2e] bg-[#1c281c] text-[#bbb]" data-testid="demarc-reset">↺ Recomeçar linhas</button>
        </div>
        {s?.medOn && (
          <div className="flex gap-2 items-center px-3 pb-2 bg-[#151d15]">
            <span className="text-[11px] font-bold text-[#bbb] flex-shrink-0">📏 Calibração — o trecho marcado mede:</span>
            <input value={medidorCm} onChange={(e) => onCmChange(e.target.value)} type="number" inputMode="decimal" placeholder="cm (fixa a escala)"
              className="flex-1 rounded-lg border border-[#2e3e2e] bg-[#0f150f] text-white text-[13px] font-bold px-2.5 py-1.5 w-full" data-testid="demarc-cm-input" />
          </div>
        )}
        <div className="bg-[#151d15] px-3 pb-3">
          <div className="flex gap-2">
            <div className="flex-1 rounded-xl py-2.5 px-2 text-center bg-[#14331e] border border-green-600">
              <div className="text-xl font-extrabold text-green-400" data-testid="demarc-pct-consumido">{pcts.p1}</div>
              <div className="text-[11px] font-extrabold min-h-[14px]">{pcts.c1}</div>
              <div className="text-[9.5px] text-[#cfcfcf] font-semibold mt-0.5">DA SAFRA CONSUMIDO<br />(linha 1 → 2)</div>
            </div>
            <div className="flex-1 rounded-xl py-2.5 px-2 text-center bg-[#33240f] border border-amber-500">
              <div className="text-xl font-extrabold text-amber-400" data-testid="demarc-pct-aconsumir">{pcts.p2}</div>
              <div className="text-[11px] font-extrabold min-h-[14px]">{pcts.c2}</div>
              <div className="text-[9.5px] text-[#cfcfcf] font-semibold mt-0.5">AINDA A CONSUMIR<br />(linha 2 → 3)</div>
            </div>
          </div>
        </div>
        <div className="flex gap-2 px-3 pb-4 bg-[#151d15]">
          <Button variant="outline" onClick={onClose} className="flex-1 bg-[#222] text-[#ccc] border-[#444]" data-testid="demarc-cancelar-btn">↩️ Voltar</Button>
          <Button onClick={salvar} className="flex-1 bg-green-700 hover:bg-green-800 font-extrabold" data-testid="demarc-salvar-btn">✅ Salvar demarcação</Button>
        </div>
      </div>
    </div>
  );
}
